import { Link } from 'react-router-dom';
import { Trash2, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="cart-empty">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <ShoppingBag className="w-20 h-20 mx-auto mb-6 text-brand-walnut opacity-20" />
          <h2 className="text-3xl font-heading font-bold text-brand-pearl mb-4">Your Cart is Empty</h2>
          <p className="text-brand-walnut mb-8">Start shopping to add items to your cart.</p>
          <Link
            to="/shop"
            data-testid="browse-products-btn"
            className="inline-flex items-center px-8 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors"
          >
            Browse Products
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="cart-page">
      <section className="section-spacing">
        <div className="container-custom">
          <h1 className="text-4xl font-heading font-bold text-brand-pearl mb-8" data-testid="cart-heading">
            Shopping Cart ({getCartCount()} items)
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, idx) => (
                <motion.div
                  key={`${item.id}-${item.size}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                  className="bg-brand-ivory rounded-sm border border-brand-pearl/10 p-6"
                  data-testid={`cart-item-${idx}`}
                >
                  <div className="flex gap-6">
                    <div className="w-24 h-24 flex-shrink-0">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-sm"
                      />
                    </div>

                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-lg font-heading font-semibold text-brand-pearl">
                            {item.name}
                          </h3>
                          <p className="text-sm text-brand-walnut">Size: {item.size}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id, item.size)}
                          data-testid={`remove-item-${idx}`}
                          className="p-2 text-brand-walnut hover:text-brand-wine transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => updateQuantity(item.id, item.size, item.quantity - 1)}
                            data-testid={`decrease-qty-${idx}`}
                            className="w-8 h-8 flex items-center justify-center border border-brand-pearl/20 rounded-sm hover:bg-brand-timberwolf transition-colors"
                          >
                            -
                          </button>
                          <span className="text-brand-pearl font-medium" data-testid={`item-qty-${idx}`}>{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.size, item.quantity + 1)}
                            data-testid={`increase-qty-${idx}`}
                            className="w-8 h-8 flex items-center justify-center border border-brand-pearl/20 rounded-sm hover:bg-brand-timberwolf transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <p className="text-lg font-bold text-brand-pearl" data-testid={`item-total-${idx}`}>
                          ₹{(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-brand-timberwolf rounded-sm border border-brand-pearl/10 p-6">
                  <h2 className="text-2xl font-heading font-bold text-brand-pearl mb-6">Order Summary</h2>

                  <div className="space-y-3 mb-6 pb-6 border-b border-brand-pearl/10">
                    <div className="flex justify-between text-brand-walnut">
                      <span>Subtotal</span>
                      <span data-testid="cart-subtotal">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-brand-walnut">
                      <span>Shipping</span>
                      <span>{getCartTotal() > 2000 ? 'Free' : '₹100'}</span>
                    </div>
                  </div>

                  <div className="flex justify-between text-xl font-bold text-brand-pearl mb-6">
                    <span>Total</span>
                    <span data-testid="cart-total">
                      ₹{(getCartTotal() + (getCartTotal() > 2000 ? 0 : 100)).toLocaleString()}
                    </span>
                  </div>

                  <Link
                    to="/checkout"
                    data-testid="proceed-checkout-btn"
                    className="w-full flex items-center justify-center px-8 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors"
                  >
                    Proceed to Checkout
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>

                  <Link
                    to="/shop"
                    data-testid="continue-shopping-btn"
                    className="block text-center mt-4 text-brand-walnut hover:text-brand-pearl transition-colors"
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;